import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';
import { DateRangeFilter } from '@/components/po-grn/DateRangeFilter';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ShoppingCart,
  Truck,
  ScanLine,
  ClipboardList,
  Receipt,
  Download,
  Loader2,
  FileSpreadsheet,
  ArrowRight,
  Scale,
  Store,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REPORTS = [
  {
    key: 'sales-analytics',
    title: 'Warehouse to Store Sales Analytics Dashboard',
    description: 'Sales overview, store performance, retailer margin at MRP, OLTOWNS margin, transaction report and data-quality exceptions — finalized invoices only.',
    icon: Store,
    accent: 'orange',
    classes: {
      card: 'border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50',
      iconWrap: 'bg-[#3b2416]',
      btn: 'bg-orange-600 hover:bg-orange-700 text-white',
    },
    navigateTo: '/reports/sales-analytics',
  },
  {
    key: 'po-grn-analytics',
    title: 'PO vs GRN Analytical Dashboard',
    description: 'Analytics module — reconciliation, price variance, MRP & margin impact, supplier performance and exceptions with global filters.',
    icon: Scale,
    accent: 'indigo',
    classes: {
      card: 'border-indigo-300 bg-gradient-to-br from-indigo-50 to-slate-50',
      iconWrap: 'bg-slate-900',
      btn: 'bg-slate-900 hover:bg-slate-800 text-white',
    },
    navigateTo: '/reports/po-grn-analytics',
  },
  {
    key: 'all-purchase-orders',
    title: 'All Purchase Orders',
    description: 'Every PO with full per-line product detail (supplier, rate, GST, totals). Filter by PO date.',
    dateFilter: true,
    icon: ShoppingCart,
    accent: 'amber',
    classes: {
      card: 'border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50',
      iconWrap: 'bg-amber-500',
      btn: 'bg-amber-600 hover:bg-amber-700 text-white',
    },
  },
  {
    key: 'all-grn',
    title: 'All GRN',
    description: 'Every GRN with invoice details, PO vs GRN MRP, prices incl GST, batch and qty splits — preview before download.',
    icon: Truck,
    accent: 'emerald',
    classes: {
      card: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50',
      iconWrap: 'bg-emerald-600',
      btn: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    },
    navigateTo: '/reports/all-grn',
  },
  {
    key: 'all-stock-audits',
    title: 'All Stock Audits',
    description: 'Every audit session + entry with system vs physical qty, variance, ageing.',
    icon: ScanLine,
    accent: 'pink',
    classes: {
      card: 'border-pink-200 bg-gradient-to-br from-pink-50 to-rose-50',
      iconWrap: 'bg-pink-600',
      btn: 'bg-pink-600 hover:bg-pink-700 text-white',
    },
  },
  {
    key: 'all-franchisee-orders',
    title: 'All Franchisee Orders',
    description: 'Dashboard + filters over every franchise order with MRP/barcode at order time — preview before download.',
    icon: ClipboardList,
    accent: 'indigo',
    classes: {
      card: 'border-indigo-200 bg-gradient-to-br from-indigo-50 to-violet-50',
      iconWrap: 'bg-indigo-600',
      btn: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    },
    navigateTo: '/reports/all-franchisee-orders',
  },
  {
    key: 'sales-fulfillment',
    title: 'Sales Fulfillment Report',
    description: 'Unified view of every Invoice + Delivery Challan with filters, search, summary KPIs and Excel/PDF export.',
    icon: Receipt,
    accent: 'blue',
    classes: {
      card: 'border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50',
      iconWrap: 'bg-blue-600',
      btn: 'bg-blue-600 hover:bg-blue-700 text-white',
    },
    navigateTo: '/reports/sales-fulfillment',
  },
  {
    key: 'fulfillment-variance',
    title: 'Fulfillment Variance Report',
    description: 'Ordered vs Supplied deltas by franchise, product and reason — spot chronically under-shipped SKUs.',
    icon: Receipt,
    accent: 'teal',
    classes: {
      card: 'border-teal-200 bg-gradient-to-br from-teal-50 to-emerald-50',
      iconWrap: 'bg-teal-600',
      btn: 'bg-teal-600 hover:bg-teal-700 text-white',
    },
    navigateTo: '/reports/fulfillment-variance',
  },
];

const Reports = () => {
  const [downloading, setDownloading] = useState(null);
  const [poRange, setPoRange] = useState({ from: '', to: '', preset: '' });
  const navigate = useNavigate();

  const handleDownload = async (report) => {
    if (report.navigateTo) {
      navigate(report.navigateTo);
      return;
    }
    setDownloading(report.key);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (report.dateFilter && poRange.from) params.date_from = poRange.from;
      if (report.dateFilter && poRange.to) params.date_to = poRange.to;
      const response = await axios.get(`${API}/reports/${report.key}.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `${report.key}_${stamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${report.title} exported`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : `Failed to download ${report.title}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileSpreadsheet className="text-emerald-600" size={32} />
            Reports
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Download a single Excel or CSV file per document type with every record and its full
            line-item product detail. One row per (document × product) — ideal for offline
            analysis, audit and compliance review.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          const isLoading = downloading === r.key;
          return (
            <Card
              key={r.key}
              className={`border-2 transition-shadow hover:shadow-lg ${r.classes.card}`}
              data-testid={`report-card-${r.key}`}
            >
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shrink-0 ${r.classes.iconWrap}`}>
                    <Icon size={24} />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-slate-900 leading-tight">
                      {r.title}
                    </h2>
                    <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                      {r.description}
                    </p>
                  </div>
                </div>
                {r.dateFilter && (
                  <div className="mb-3" data-testid={`report-date-filter-${r.key}`}>
                    <DateRangeFilter label="PO Date" from={poRange.from} to={poRange.to} preset={poRange.preset}
                      onChange={setPoRange} testId={`report-${r.key}-date`} />
                  </div>
                )}
                <div className="mt-auto pt-2 flex gap-2">
                  <Button
                    onClick={() => handleDownload(r)}
                    disabled={isLoading}
                    className={`flex-1 gap-2 ${r.classes.btn}`}
                    data-testid={`report-download-${r.key}`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Preparing…
                      </>
                    ) : r.navigateTo ? (
                      <>
                        <ArrowRight size={16} />
                        Open Report
                      </>
                    ) : (
                      <>
                        <Download size={16} />
                        Download Excel
                      </>
                    )}
                  </Button>
                  {!r.navigateTo && (
                    <CsvDownloadButton onDownload={() => handleDownload(r)} size="default" disabled={isLoading}
                      testId={`report-download-csv-${r.key}`} className="bg-white" />
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-xs text-slate-500 italic mt-2">
        Each download contains all records of that document type. Files are generated in
        real time, so the latest data is always included.
      </div>
    </div>
  );
};

export default Reports;
